// Matches the shop's configured money format so the amount in the progress
// message reads identically to every other price in the drawer. Falling back to
// the browser locale would render "54,00 US$" next to the theme's "$54.00".
function formatMoney(cents) {
  var format = window.themeVariables
    && window.themeVariables.settings
    && window.themeVariables.settings.moneyFormat;

  if (format) {
    var amount = (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
  }

  var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: currency });
}

function scoreCandidate(price, gap) {
  if (price >= gap) {
    return price - gap;
  }
  return (gap - price) * 1.5;
}

// Kivetz/Urminsky/Zheng (2006) found perceived progress alone speeds goal
// completion ~20%. A bar reading 8% looks like nothing has happened, so give
// it a floor — the shopper always sees the goal as underway.
var MINIMUM_VISIBLE_PROGRESS = 0.12;

// Rank by how cleanly a product closes the gap. 2.0 rather than 1.6 because a
// tighter band kept excluding near-misses — at a $20 gap, 1.6 capped
// suggestions at $32 and dropped a $34 item over two dollars.
var MAX_OVERSHOOT_RATIO = 2.0;
var MINIMUM_SUGGESTIONS = 2;

// Four fit the drawer at a legible size; the extra two are what the arrows
// reveal, so the row has somewhere to go without becoming an endless list.
var MAXIMUM_SUGGESTIONS = 6;

// The cart drawer rebuilds this element from server HTML after every cart
// change, so anything that must survive a rebuild lives here rather than on the
// instance.
var lastTopPick = null;
var pendingFocusProductId = null;

class FreeShippingUpsell extends HTMLElement {
  static get observedAttributes() {
    return ['threshold', 'total-price'];
  }

  constructor() {
    super();
    this._onCartChanged = this._onCartChanged.bind(this);
    this._onCartError = this._onCartError.bind(this);
    this._lastTopPick = null;
  }

  connectedCallback() {
    // A blank or unparseable threshold setting ("1,000", "$100") makes Liquid
    // emit 0, which would read as "already qualified" and promise every shopper
    // free shipping the store then has to honour. Fail closed instead.
    var rawThreshold = this.threshold;
    if (!isFinite(rawThreshold) || rawThreshold <= 0) {
      this.hidden = true;
      return;
    }

    this.threshold = Math.round(rawThreshold * ((window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1));
    document.addEventListener('cart:change', this._onCartChanged);
    this.addEventListener('cart:error', this._onCartError);

    var initialIds = (this.getAttribute('cart-product-ids') || '').split(',').filter(Boolean);
    var run = this._updateSelection.bind(this, initialIds);

    // This drawer is fixed-position and inside the viewport rect even when
    // closed, so revealing cards on load would start fetching every thumbnail
    // on every page of the store for a drawer most shoppers never open.
    var drawer = this.closest('cart-drawer');

    if (drawer && !drawer.hasAttribute('open')) {
      // Watching the attribute rather than listening for dialog:after-show:
      // measured on Impact 7.0.1, this drawer emits dialog:after-hide when it
      // closes but nothing at all when it opens, so an event listener here waits
      // forever and the row never appears. The attribute flips on every version.
      var self = this;
      this._openObserver = new MutationObserver(function () {
        if (!drawer.hasAttribute('open')) return;
        self._openObserver.disconnect();
        self._openObserver = null;
        run();
      });
      this._openObserver.observe(drawer, { attributes: true, attributeFilter: ['open'] });
    } else {
      run();
    }

    this._restorePendingFocus();
  }

  disconnectedCallback() {
    document.removeEventListener('cart:change', this._onCartChanged);
    this.removeEventListener('cart:error', this._onCartError);

    // The drawer replaces its inner HTML on every cart change, so instances are
    // discarded constantly; an observer left running holds each one alive.
    if (this._openObserver) {
      this._openObserver.disconnect();
      this._openObserver = null;
    }
  }

  get threshold() {
    return parseFloat(this.getAttribute('threshold'));
  }

  set threshold(value) {
    this.setAttribute('threshold', value);
  }

  get totalPrice() {
    return parseFloat(this.getAttribute('total-price'));
  }

  set totalPrice(value) {
    this.setAttribute('total-price', value);
  }

  async attributeChangedCallback(attribute, oldValue, newValue) {
    if (oldValue === newValue) return;

    await window.customElements.whenDefined('progress-bar');

    // The drawer's re-render can detach this element while the await is
    // pending; writing to the orphaned bar afterwards is wasted work.
    if (!this.isConnected) return;

    var progressBarElement = this.querySelector('progress-bar');

    if (attribute === 'threshold' && progressBarElement) {
      progressBarElement.valueMax = newValue;
    }

    if (attribute === 'total-price' && progressBarElement) {
      progressBarElement.valueNow = newValue;
    }

    this._clampProgress();
    this._updateMessage();
  }

  _clampProgress() {
    var progressBarElement = this.querySelector('progress-bar');
    if (!progressBarElement || this.threshold <= 0) return;

    var actual = this.totalPrice / this.threshold;

    if (actual > 0 && actual < MINIMUM_VISIBLE_PROGRESS) {
      progressBarElement.style.setProperty('--progress', MINIMUM_VISIBLE_PROGRESS);
      // The clamp deliberately overstates the fill, so give assistive tech the
      // real figure rather than the flattering one.
      progressBarElement.setAttribute('aria-valuetext', formatMoney(this.totalPrice) + ' of ' + formatMoney(this.threshold));
    }
  }

  // An add can be rejected after the click — sold out in the meantime, or an
  // order-limit app blocking it. Without a buy-buttons wrapper nothing in the
  // theme surfaces that, so the button would just silently re-enable.
  _onCartError(event) {
    var messageElement = this.querySelector('.free-shipping-upsell__bar > span');
    if (!messageElement || !event.detail || !event.detail.error) return;

    messageElement.textContent = event.detail.error;
  }

  // The heading has to stay true to what the row can actually deliver. When
  // nothing in the pool closes the gap in one add, promising exactly that is
  // the fastest way to teach shoppers to ignore the whole block.
  _updateHeading(hasSingleAddClose) {
    var headingElement = this.querySelector('.free-shipping-upsell__eyebrow');
    if (!headingElement) return;

    var attribute = hasSingleAddClose ? 'heading-can-close' : 'heading-cannot-close';
    var heading = this.getAttribute(attribute);

    if (heading) headingElement.textContent = heading;
  }

  _updateMessage() {
    var messageElement = this.querySelector('.free-shipping-upsell__bar > span');
    if (!messageElement) return;

    if (this.totalPrice >= this.threshold) {
      messageElement.innerHTML = this.getAttribute('reached-message');
    } else {
      var remaining = formatMoney(this.threshold - this.totalPrice).replace(/\$/g, '$$$$');
      var replacement = '<span class="bold text-accent">' + remaining + '</span>';
      // Anchored to a single placeholder — a greedy match would swallow
      // everything between two placeholders in a translated string.
      messageElement.innerHTML = this.getAttribute('unreached-message').replace(/\{\{\s*\w+\s*\}\}/, replacement);
    }
  }

  _onCartChanged(event) {
    // cart:change is a public event and the installed cart apps can re-dispatch
    // it with a partial payload. Bail rather than throw, which would leave the
    // row showing a selection for the previous cart.
    var cart = event.detail && event.detail.cart;
    if (!cart || !Array.isArray(cart.items)) return;

    var items = cart.items;

    var priceForItems = items
      .filter(function (item) { return item.requires_shipping; })
      .reduce(function (sum, item) { return sum + item.final_line_price; }, 0);

    var cartDiscount = (cart.cart_level_discount_applications || [])
      .reduce(function (sum, discount) { return sum + discount.total_allocated_amount; }, 0);

    // A discount allocated to non-shippable lines (gift cards) can exceed the
    // shippable subtotal and drive this negative, which would invert the gap.
    this.totalPrice = Math.max(0, priceForItems - cartDiscount);
    this._rememberFocusedCard();
    this._updateSelection(items.map(function (item) { return String(item.product_id); }));
  }

  // The drawer replaces its whole inner HTML on every cart change, so the
  // button the shopper just pressed is destroyed under them and focus falls to
  // the body. Note which card had focus so the rebuilt row can take it back.
  _rememberFocusedCard() {
    var active = document.activeElement;
    if (!active || !active.closest) return;

    var card = active.closest('.free-shipping-upsell__item');
    pendingFocusProductId = card ? card.dataset.productId : null;
  }

  _restorePendingFocus() {
    if (!pendingFocusProductId) return;

    var card = this.querySelector('.free-shipping-upsell__item[data-product-id="' + pendingFocusProductId + '"]');
    pendingFocusProductId = null;

    if (!card || card.hidden) return;

    var button = card.querySelector('.button');
    if (button) button.focus();
  }

  _updateSelection(cartProductIds) {
    // Re-selecting while a quick-buy dialog is open would hide or move the card
    // hosting it, leaving the page scroll-locked behind an invisible modal.
    if (this.querySelector('quick-buy-drawer[open]')) return;

    var gap = this.threshold - this.totalPrice;
    var cardsWrapper = this.querySelector('.free-shipping-upsell__cards');
    var list = this.querySelector('.free-shipping-upsell__track');

    // A single unparseable price would make every comparison in the backfill
    // sort return NaN, silently degrading the comparator and leaving the row in
    // DOM order with no error.
    var candidates = Array.prototype.slice
      .call(this.querySelectorAll('.free-shipping-upsell__item'))
      .filter(function (el) { return isFinite(parseFloat(el.dataset.price)); });

    if (candidates.length === 0) return;

    if (gap <= 0) {
      if (cardsWrapper) cardsWrapper.hidden = true;
      candidates.forEach(function (el) { el.hidden = true; });
      lastTopPick = null;
      this._lastTopPick = null;
      return;
    }

    var excludeSet = new Set(cartProductIds);

    var eligible = candidates.filter(function (el) {
      if (excludeSet.has(el.dataset.productId)) return false;
      // A ranged product is priced from its cheapest variant, so it can be
      // admitted on one price and added at another. It can still appear as
      // filler, it just cannot carry the one-add promise.
      if (el.dataset.priceVaries === 'true') return false;
      var price = parseFloat(el.dataset.price);
      return price >= gap && price <= gap * MAX_OVERSHOOT_RATIO;
    });

    var ranked = eligible
      .map(function (el) { return { el: el, score: scoreCandidate(parseFloat(el.dataset.price), gap) }; })
      .sort(function (a, b) { return a.score - b.score; });

    // Backfill so the row is never half-empty. A well-spread pool means fewer
    // products land inside any one band, so without this the better the price
    // ladder gets, the emptier the row looks.
    if (ranked.length < MAXIMUM_SUGGESTIONS) {
      var alreadyRanked = new Set(ranked.map(function (entry) { return entry.el; }));

      var filler = candidates
        .filter(function (el) { return !excludeSet.has(el.dataset.productId) && !alreadyRanked.has(el); })
        .map(function (el) { return { el: el, score: scoreCandidate(parseFloat(el.dataset.price), gap) }; })
        .sort(function (a, b) { return a.score - b.score; });

      ranked = ranked.concat(filler.slice(0, MAXIMUM_SUGGESTIONS - ranked.length));
    }

    // Whether the heading can promise a one-add unlock depends on what the
    // shopper will actually see, not on which products cleared the band — a
    // product far above the gap still closes it, it just overshoots.
    var visible = ranked.slice(0, MAXIMUM_SUGGESTIONS);
    var hasSingleAddClose = visible.some(function (entry) {
      return entry.el.dataset.priceVaries !== 'true'
        && parseFloat(entry.el.dataset.price) >= gap;
    });

    this._updateHeading(hasSingleAddClose);

    candidates.forEach(function (el) { el.hidden = true; });

    var topPick = ranked.length ? ranked[0].el.dataset.productId : null;
    var topPickChanged = topPick !== lastTopPick;
    lastTopPick = topPick;
    this._lastTopPick = topPick;

    visible.forEach(function (entry) {
      entry.el.hidden = false;
      // Reorder in the DOM so the best-fit match is visually first, not
      // whichever came first in the merchant's pool order.
      if (list) list.appendChild(entry.el);
    });

    // ScrollCarousel memoises its item list on connect and derives scroll
    // positions from offsetLeft. Reordering leaves that cache in the old order
    // while offsets follow the new one, so its index maths reads positions out
    // of sequence — the chevrons jump the wrong way and scroll-end can land on
    // an index with no slide. Drop the cache so it rebuilds from the live DOM.
    if (list) {
      list._items = null;
      list._selectedIndex = 0;
    }

    // Only snap back to the start when the best match actually changed. Doing
    // it on every cart update yanked the row out from under anyone who had
    // scrolled, right at the moment they were reaching for a product.
    if (list && topPickChanged) list.scrollLeft = 0;

    // One weak suggestion discredits the whole set, so below the floor show none.
    if (cardsWrapper) cardsWrapper.hidden = ranked.length < MINIMUM_SUGGESTIONS;
  }
}

if (!window.customElements.get('free-shipping-upsell')) {
  window.customElements.define('free-shipping-upsell', FreeShippingUpsell);
}